import React, { useEffect, useMemo, useState, Suspense, lazy, createElement } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDownIcon, SlidersHorizontalIcon } from 'lucide-react';
import { Header } from './components/Header';
import { HeroSection } from './components/HeroSection';
import { FilterBar } from './components/FilterBar';
import { SchoolListings } from './components/SchoolListings';
import { ContentSection } from './components/ContentSection';
import { SeoContentSection } from './components/SeoContentSection';
import { Footer } from './components/Footer';
// Code-split route bundles — only the home page ships with the initial bundle.
const ArticlesPage = lazy(() => import('./pages/ArticlesPage').then(m => ({
  default: m.ArticlesPage
})));
const CollegesPage = lazy(() => import('./pages/CollegesPage').then(m => ({
  default: m.CollegesPage
})));
const ArticleDetailPage = lazy(() => import('./pages/ArticleDetailPage').then(m => ({
  default: m.ArticleDetailPage
})));
const ProgramLandingPage = lazy(() => import('./pages/ProgramLandingPage').then(m => ({
  default: m.ProgramLandingPage
})));
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({
  default: m.TermsPage
})));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage').then(m => ({
  default: m.CookiePolicyPage
})));
const CANoticePage = lazy(() => import('./pages/CANoticePage').then(m => ({
  default: m.CANoticePage
})));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then(m => ({
  default: m.PrivacyPolicyPage
})));
const RequestInformationPage = lazy(() => import('./pages/RequestInformationPage').then(m => ({
  default: m.RequestInformationPage
})));
const RequestInformationFelonAssistancePage = lazy(() => import('./pages/RequestInformationFelonAssistancePage').then(m => ({
  default: m.RequestInformationFelonAssistancePage
})));
const ExploreBridgePage = lazy(() => import('./pages/ExploreBridgePage').then(m => ({
  default: m.ExploreBridgePage
})));
const RequestInformationV1Page = lazy(() => import('./pages/RequestInformationV1Page').then(m => ({
  default: m.RequestInformationV1Page
})));
const RequestInformationV3Page = lazy(() => import('./pages/RequestInformationV3Page').then(m => ({
  default: m.RequestInformationV3Page
})));
const FindDegreesBridgePage = lazy(() => import('./pages/FindDegreesBridgePage').then(m => ({
  default: m.FindDegreesBridgePage
})));
const AdvertiserTemplatePage = lazy(() => import('./pages/AdvertiserTemplatePage').then(m => ({
  default: m.AdvertiserTemplatePage
})));
const PartnerCollegesPage = lazy(() => import('./pages/PartnerCollegesPage').then(m => ({
  default: m.PartnerCollegesPage
})));
import { useSchoolSearch } from './hooks/useSchoolSearch';
import { useGeoLocation } from './hooks/useGeoLocation';
import { useMetaTags } from './hooks/useMetaTags';
import { useMetaPixel } from './hooks/useMetaPixel';
import { useUrlParams } from './hooks/useUrlParams';
import { areaOfStudyData } from './data/areaOfStudyData';
import { useGoogleTagManager } from './hooks/useGoogleTagManager';
import { getDefaultProgram, getAreaFromProgram, isValidProgram } from './data/filterOptions';
function FilterAccordion({
  degreeLevel,
  selectedAreaId,
  selectedProgram,
  loading,
  schoolCount,
  onDegreeLevelChange,
  onAreaStudyChange,
  onProgramChange
}: {
  degreeLevel: number;
  selectedAreaId: number;
  selectedProgram: number;
  loading: boolean;
  schoolCount: number;
  onDegreeLevelChange: (v: number) => void;
  onAreaStudyChange: (v: number) => void;
  onProgramChange: (v: number) => void;
}) {
  const [isOpen, setIsOpen] = useState(true);
  return <div className="w-full bg-white border-b border-gray-200 shadow-sm relative z-30" data-id="element-0" data-mp="6qiavrwewmt5">
      {/* Accordion toggle — works on both mobile and desktop */}
      <div className="max-w-7xl mx-auto px-4" data-id="element-1" data-mp="qy73304ysz81">
        <button type="button" onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between py-3 md:py-4 hover:bg-gray-50/50 transition-colors rounded-lg px-2" data-id="element-2" data-mp="qyyq3mkhxomp">
          <div className="flex items-center gap-2.5" data-id="element-3" data-mp="c8rg7v8k1dxm">
            <SlidersHorizontalIcon className="w-4 h-4 text-[#3b82f6]" data-id="element-4" data-mp="ca2itrq9kfn5" />
            <span className="text-sm font-bold text-gray-900" data-id="element-5" data-mp="19uygisxtsk46">
              Find Your Program
            </span>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full" data-id="element-6" data-mp="14xg6t0bzi0v4">
              {loading ? '...' : `${schoolCount} result${schoolCount !== 1 ? 's' : ''}`}
            </span>
          </div>
          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-7" data-mp="3knt0cx1gsnnc" />
        </button>
      </div>

      {/* Collapsible content */}
      <div className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[500px] opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'}`} data-id="element-8" data-mp="1w4yk20vebtzi">
        <div className="px-4 pb-4 pt-1 max-w-7xl mx-auto" data-id="element-9" data-mp="2553gjfz1x7yw">
          <p className="text-xs text-gray-500 mb-3" data-id="element-10" data-mp="253k1fsp4t2yp">
            Select your degree level, area of interest, and program to see
            matching schools.
          </p>

          <FilterBar degreeLevel={degreeLevel} areaStudy={selectedAreaId} program={selectedProgram} onDegreeLevelChange={onDegreeLevelChange} onAreaStudyChange={onAreaStudyChange} onProgramChange={onProgramChange} data-id="element-11" data-mp="2evppirc9j08m" />

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 pt-3 border-t border-gray-100" data-id="element-12" data-mp="2suxu5zr730k7">
            <span className="text-xs text-gray-500 flex items-center gap-1.5" data-id="element-13" data-mp="timykzu9v4we">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" data-id="element-14" data-mp="yg4yy1u84bss"></span>
              All programs are accredited
            </span>
            <span className="text-xs text-gray-400" data-id="element-15" data-mp="21s3xd3ubuqmm">•</span>
            <span className="text-xs text-gray-500" data-id="element-16" data-mp="1wt0qdduseac0">100% online options</span>
            <span className="text-xs text-gray-400 hidden sm:inline" data-id="element-17" data-mp="2as8w6plgggp6">•</span>
            <span className="text-xs text-gray-500 hidden sm:inline" data-id="element-18" data-mp="2aq5tp6348v3k">
              Financial aid eligible
            </span>
          </div>
        </div>
      </div>
    </div>;
}
export function App() {
  // Google Tag Manager container (head script + body noscript iframe).
  useGoogleTagManager();
  // Set favicon
  useEffect(() => {
    const favicon = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
    favicon.type = 'image/svg+xml';
    favicon.rel = 'icon';
    favicon.href = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><text y='0.9em' font-size='90'>🎓</text></svg>";
    if (!document.querySelector("link[rel*='icon']")) {
      document.head.appendChild(favicon);
    }
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const urlParams = useUrlParams();
  // Derive initial state from URL params or use defaults
  const initialProgram = urlParams.mProgram && isValidProgram(urlParams.mProgram) ? urlParams.mProgram : 900;
  const initialArea = urlParams.mProgram ? getAreaFromProgram(urlParams.mProgram) : 9;
  // Default the degree level filter to Bachelor's (value 3) when no
  // degreeInterest is supplied via URL params.
  const initialDegree = urlParams.degreeInterest ?? 3;
  // Derive currentPage from URL path
  const getPageFromPath = (pathname: string) => {
    const path = pathname.replace(/^\/+|\/+$/g, '');
    // Handle dynamic routes
    const pathMap: Record<string, string> = {
      'request-information': 'request-information',
      articles: 'articles',
      colleges: 'colleges',
      terms: 'terms',
      'privacy-policy': 'privacy-policy',
      'cookie-policy': 'cookie-policy',
      'ca-notice': 'ca-notice',
      'program-landing': 'program-landing',
      'partner-colleges': 'partner-colleges'
    };
    return pathMap[path] || 'compare';
  };
  const [currentPage, setCurrentPage] = useState<string>(getPageFromPath(location.pathname));
  const [currentArticle, setCurrentArticle] = useState<string | null>(null);
  const [degreeLevel, setDegreeLevel] = useState<number>(initialDegree);
  const [selectedAreaId, setSelectedAreaId] = useState<number>(initialArea);
  const [selectedProgram, setSelectedProgram] = useState<number>(initialProgram);
  // Sync currentPage when location changes (e.g. Magic Patterns route dropdown)
  useEffect(() => {
    setCurrentPage(getPageFromPath(location.pathname));
  }, [location.pathname]);
  // Sync filter state when URL params change (e.g. arriving from /find-degrees/v6)
  useEffect(() => {
    if (urlParams.mProgram && isValidProgram(urlParams.mProgram)) {
      setSelectedProgram(urlParams.mProgram);
      setSelectedAreaId(getAreaFromProgram(urlParams.mProgram));
    }
    if (urlParams.degreeInterest != null) {
      setDegreeLevel(urlParams.degreeInterest);
    }
  }, [urlParams.mProgram, urlParams.degreeInterest]);
  // Build tracking params from URL (falls back to defaults inside the hook)
  const tracking = useMemo(() => ({
    sub1: urlParams.sub1 ?? undefined,
    sub2: urlParams.sub2 ?? undefined,
    sub3: urlParams.sub3 ?? undefined
  }), [urlParams]);
  const {
    geo
  } = useGeoLocation();
  useMetaPixel();
  const {
    schools,
    loading,
    error
  } = useSchoolSearch({
    degreeInterest: degreeLevel,
    mProgram: selectedProgram,
    zipCode: geo.zipCode,
    tracking
  });
  const currentAreaData = areaOfStudyData[selectedAreaId];
  const metaConfig = useMemo(() => {
    const configs: Record<string, {
      title: string;
      description: string;
      keywords: string;
    }> = {
      compare: {
        title: `Compare Online ${currentAreaData.name} Programs | DegreeCompare`,
        description: `Compare top accredited online ${currentAreaData.name.toLowerCase()} programs. Find the best online colleges, read reviews, and explore degree options for ${new Date().getFullYear()}.`,
        keywords: `online ${currentAreaData.name.toLowerCase()} degree, accredited online programs, compare online colleges, ${currentAreaData.name.toLowerCase()} programs, online education`
      },
      colleges: {
        title: 'Explore Accredited Online Colleges | DegreeCompare',
        description: 'Browse top-rated accredited online colleges and universities. Compare programs, read reviews, and find the right school for your online degree.',
        keywords: 'online colleges, accredited universities, online degree programs, best online schools, college directory'
      },
      articles: {
        title: 'Articles & Guides for Online Students | DegreeCompare',
        description: 'Expert advice on choosing the right online program, financial aid, career planning, and more. Resources for prospective and current online students.',
        keywords: 'online education articles, college guides, financial aid tips, career planning, online degree advice'
      }
    };
    return configs[currentPage] || configs.compare;
  }, [currentPage, currentAreaData]);
  useMetaTags({
    title: metaConfig.title,
    description: metaConfig.description,
    keywords: metaConfig.keywords,
    ogTitle: metaConfig.title,
    ogDescription: metaConfig.description
  });
  const handleAreaChange = (areaId: number) => {
    setSelectedAreaId(areaId);
    setSelectedProgram(getDefaultProgram(areaId));
  };
  const handleProgramChange = (programId: number) => {
    // Derive the area from the program
    const newAreaId = getAreaFromProgram(programId);
    // If the area changes, update both area and program
    if (newAreaId !== selectedAreaId) {
      setSelectedAreaId(newAreaId);
    }
    setSelectedProgram(programId);
  };
  const handleNavigate = (page: string, articleSlug?: string) => {
    setCurrentPage(page);
    if (articleSlug) {
      setCurrentArticle(articleSlug);
    } else {
      setCurrentArticle(null);
    }
    // Update URL to match the current page
    let urlPath = '/';
    if (page === 'compare') {
      urlPath = '/';
    } else if (page === 'articles' && articleSlug) {
      // Article detail pages live at /articles/<slug>
      urlPath = `/articles/${articleSlug}`;
    } else if (page.startsWith('university/')) {
      urlPath = `/${page}`;
    } else {
      urlPath = `/${page}`;
    }
    navigate(urlPath);
    window.scrollTo(0, 0);
  };
  const handleNavigateToProgram = (areaId: number, programId: number) => {
    setSelectedAreaId(areaId);
    setSelectedProgram(programId);
    // Stay on compare page to show filtered results
    window.scrollTo(0, 0);
  };
  const handleNavigateToDegree = (degree: number) => {
    setDegreeLevel(degree);
    setSelectedAreaId(9);
    setSelectedProgram(900);
    setCurrentPage('compare');
    navigate('/');
    window.scrollTo(0, 0);
  };
  // Shared layout wrapper
  const isAdvertiserPage = location.pathname.startsWith('/universities/');
  // Explore bridge pages render their own nav + footer.
  const isBridgePage = location.pathname.startsWith('/find-degrees/') || location.pathname.startsWith('/explore/');
  // The v3 request-information template renders its own header + footer.
  const isSelfContainedTemplate = location.pathname.startsWith('/request-information/v3');
  const PageLayout = ({
    children
  }: {
    children: React.ReactNode;
  }) => {
    if (isAdvertiserPage || isBridgePage || isSelfContainedTemplate) {
      return <div className="min-h-screen bg-gray-50 font-sans text-gray-900 flex flex-col" data-id="element-19" data-mp="wnqhnq29pdkm">
          <main className="flex-grow" data-id="element-20" data-mp="2d28k2fedcdo8">{children}</main>
        </div>;
    }
    return <div className="min-h-screen bg-gray-100 font-sans text-gray-900 flex flex-col" data-id="element-21" data-mp="284glj2j7xtb2">
        <Header currentPage={currentPage} onNavigate={handleNavigate} onNavigateToProgram={handleNavigateToProgram} onNavigateToDegree={handleNavigateToDegree} data-id="element-22" data-mp="hn36b5qqfjjg" />
        <main className="flex-grow" data-id="element-23" data-mp="qmzxbe0lguho">{children}</main>
        <Footer onNavigate={handleNavigate} data-id="element-24" data-mp="qpm6fi34vi20" />
      </div>;
  };
  const ComparePage = () => <>
      <HeroSection areaData={currentAreaData} selectedProgram={selectedProgram} degreeLevel={degreeLevel} selectedAreaId={selectedAreaId} t1Override={urlParams.t1} data-id="element-25" data-mp="vki00mo0qdh8" />
      <FilterAccordion degreeLevel={degreeLevel} selectedAreaId={selectedAreaId} selectedProgram={selectedProgram} loading={loading} schoolCount={schools.length} onDegreeLevelChange={setDegreeLevel} onAreaStudyChange={handleAreaChange} onProgramChange={handleProgramChange} data-id="element-26" data-mp="1abh53npnajw2" />
      <SchoolListings schools={schools} loading={loading} error={error} selectedAreaId={selectedAreaId} selectedProgram={selectedProgram} onProgramChange={handleProgramChange} data-id="element-27" data-mp="1ocbf2asccswu" />
      <SeoContentSection areaStudyId={selectedAreaId} topSchool={schools[0]} data-id="element-28" data-mp="1ti6tj1eu1kam" />
      <ContentSection areaData={currentAreaData} selectedAreaId={selectedAreaId} onNavigate={handleNavigate} data-id="element-29" data-mp="1ohj4gla5zdj1" />
    </>;
  return <PageLayout data-id="element-30" data-mp="2h67wzvx5maj6">
      <Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center" data-id="element-32" data-mp="2h7sr5kfk3rgi">
            <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin" data-id="element-33" data-mp="2c7wllgr7nryu" />
          </div>} data-id="element-31" data-mp="2h4n0w1v6yjj7">
        <Routes data-id="element-34" data-mp="27dio98sznarj">
          <Route path="/" element={<ComparePage data-id="element-36" data-mp="23d8inx93ymd7" />} data-id="element-35" data-mp="23ejjbo79be03" />
          <Route path="/request-information" element={<RequestInformationPage data-id="element-38" data-mp="2zwfyk9l7gsx9" />} data-id="element-37" data-mp="23e1g27m6fzio" />
          <Route path="/request-information/v1" element={<RequestInformationV1Page data-id="element-40" data-mp="2v3wi94v4vjor" />} data-id="element-39" data-mp="2zy0pzqc83vqw" />
          <Route path="/request-information/v3" element={<RequestInformationV3Page data-id="element-42" data-mp="ta0cs03nl97l" />} data-id="element-41" data-mp="t876wn0v6t0n" />
          <Route path="/request-information/felon-assistance" element={<RequestInformationFelonAssistancePage data-id="element-44" data-mp="jinlb9lrol23" />} data-id="element-43" data-mp="y7a2lguyfmvy" />
          {/* Explicit route so this template shows up in the preview's
              route list; the dynamic route below serves every other
              {brand}-{version} slug. */}
          <Route path="/explore/UAGC-v1" element={<ExploreBridgePage data-id="element-46" data-mp="fg93mstu3rce" />} data-id="element-45" data-mp="ag4jmstpwehf" />
          <Route path="/explore/UAGC-v2" element={<ExploreBridgePage data-id="element-48" data-mp="fefxden4v94x" />} data-id="element-47" data-mp="fhk9cy6ub965" />
          <Route path="/explore/UMA-v1" element={<ExploreBridgePage data-id="element-50" data-mp="kt3q76kp183" />} data-id="element-49" data-mp="jrqpx5zy4nq" />
          <Route path="/explore/UMA-v2" element={<ExploreBridgePage data-id="element-52" data-mp="5hjm515uo8wo" />} data-id="element-51" data-mp="lmb9adcvura" />
          <Route path="/explore/UMA-v3" element={<ExploreBridgePage data-id="element-54" data-mp="1uxiyss8oggh2" />} data-id="element-53" data-mp="5il5ru0xm3q0" />
          <Route path="/explore/search-v1" element={<ExploreBridgePage data-id="element-56" data-mp="1uvgaccn6aveg" />} data-id="element-55" data-mp="1uyc6bvfgna09" />
          <Route path="/explore/:bridgeSlug" element={<ExploreBridgePage data-id="element-58" data-mp="1ztzx2f9lcb7v" />} data-id="element-57" data-mp="1zwvve4pcp1wu" />
          <Route path="/colleges" element={<CollegesPage data-id="element-60" data-mp="3keo867ugyxcw" />} data-id="element-59" data-mp="3kfg63pbjh76c" />
          <Route path="/partner-colleges" element={<PartnerCollegesPage data-id="element-62" data-mp="3fgcz3zc00qvq" />} data-id="element-61" data-mp="3ffbq00ejmegn" />
          <Route path="/universities/:collegeSlug" element={<AdvertiserTemplatePage data-id="element-64" data-mp="3u4zn0pfoyqyk" />} data-id="element-63" data-mp="3fd8pgmfrl2xo" />
          <Route path="/universities/:collegeSlug/:programSlug" element={<AdvertiserTemplatePage data-id="element-66" data-mp="3pbofem32265l" />} data-id="element-65" data-mp="3pdhm21do4qdl" />
          <Route path="/articles" element={<ArticlesPage onNavigate={handleNavigate} data-id="element-68" data-mp="2xwxe2fi4sqg" />} data-id="element-67" data-mp="6ygx9drgmaga" />
          <Route path="/articles/:slug" element={<ArticleDetailPage onNavigate={handleNavigate} onProgramChange={handleProgramChange} data-id="element-70" data-mp="gwd4vvl6un9n" />} data-id="element-69" data-mp="2xn7sfydcnla" />
          <Route path="/program-landing" element={<ProgramLandingPage programId={selectedProgram} degreeLevel={degreeLevel} onProgramChange={handleProgramChange} data-id="element-72" data-mp="2dpy50q1exek3" />} data-id="element-71" data-mp="2ioj58pgnzy8j" />
          <Route path="/terms" element={<TermsPage data-id="element-74" data-mp="1wuu0krkzt5rw" />} data-id="element-73" data-mp="1wvly47y5hdkt" />
          <Route path="/cookie-policy" element={<CookiePolicyPage data-id="element-76" data-mp="21mvjgwco1uqw" />} data-id="element-75" data-mp="1wu2gacwuiugx" />
          <Route path="/ca-notice" element={<CANoticePage data-id="element-78" data-mp="21oosgiar4r22" />} data-id="element-77" data-mp="21nn4xecjuc3g" />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage data-id="element-80" data-mp="26kdj1w4smxm8" />} data-id="element-79" data-mp="26lf7r3eqfimf" />
          <Route path="/find-degrees/:templateId" element={<FindDegreesBridgePage data-id="element-82" data-mp="18z801kn4ta5u" />} data-id="element-81" data-mp="26nrk911q10s3" />
        </Routes>
      </Suspense>
    </PageLayout>;
}